import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import { on } from '@ember/modifier';
import PageSetup from 'bcl/components/page-setup';
import { get } from '@ember/helper';
import amountName from 'bcl/helpers/amount-name';
import { gte } from 'ember-truth-helpers';
import { LinkTo } from '@ember/routing';
import displayDate from 'bcl/helpers/display-date';
import filterSort from 'bcl/helpers/filter-sort';
import { array, fn } from '@ember/helper';
import { service } from '@ember/service';
import gt from 'ember-truth-helpers/helpers/gt';
import type StocktakingService from 'bcl/services/stocktaking';
import type { Store } from '@warp-drive/core';
import type ModalService from 'bcl/services/modal';
import LocationModel from 'bcl/models/location';
import type ItemModel from 'bcl/models/item';

export interface LocationSignature {
  // The arguments accepted by the component
  Args: {
    model: ItemModel[];
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class LocationComponent extends Component<LocationSignature> {
  @service declare stocktaking: StocktakingService;
  @service declare store: Store;
  @service declare modal: ModalService;

  get allLocationsToShow() {
    const fakeAll = { name: 'Gesamt', id: 0 };
    const all = [...this.store.peekAll<LocationModel>('location')].filter(
      (location) => location.active
    );
    if (all?.length > 2) {
      // all.push({ name: 'Alle', id: 0 });
      return [fakeAll];
    }
    return [...all.sort((a, b) => a.name.localeCompare(b.name)), fakeAll];
  }

  <template>
    {{pageTitle "Lagerübersicht"}}
    <PageSetup @title="Lagerübersicht">
      <div class="px-4 lg:px-8">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">

          </div>
          <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            {{#each
              (filterSort
                this.stocktaking.all "status" "created_at" eq=(array "created")
              )
              as |stocktaking idx|
            }}
              <button
                class="{{if (gt idx 0) 'ml-4'}}
                  rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                type="button"
                {{on "click" (fn this.stocktaking.resume stocktaking.id)}}
              >Inventur vom
                {{displayDate stocktaking.created_at}}
                fortsetzen</button>
            {{/each}}
            <button
              {{on "click" this.modal.toggleStocktaking}}
              type="button"
              class="inline-block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Inventur beginnen</button>
            <button
              type="button"
              {{on "click" this.modal.toggleNewOrder}}
              class="inline-block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Neue Bestellung anlegen</button>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >Name</th>
                    {{#each this.allLocationsToShow as |location|}}
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {{location.name}}</th>
                    {{/each}}
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >Soll</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >bestellt</th>

                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                      <span class="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  {{#each (filterSort @model "active" "name") as |item|}}
                    <tr class="even:bg-gray-50">
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                      >{{item.name}}
                        ({{item.gebinde}})</td>
                      {{#each this.allLocationsToShow as |location|}}
                        <td
                          class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                        >
                          {{#let
                            (get item.locationAmounts location.id)
                            as |locationAmounts|
                          }}
                            {{! falls es nicht definiert ist, ist das immer falsch }}
                            {{#if (gte locationAmounts 0)}}
                              <span class="p-1 rounded-md group">
                                {{locationAmounts}}
                                {{amountName item locationAmounts}}
                              </span>

                            {{else}}
                              <span title="nicht gezählt" class="p-1">
                                n. gez.
                              </span>
                            {{/if}}
                          {{/let}}
                        </td>
                      {{/each}}

                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {{item.displayTargetAmount}}
                        {{amountName item item.target_amount}}
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {{item.orderedAndNotArrivedAmount}}
                        {{amountName item item.orderedAndNotArrivedAmount}}
                      </td>
                      <td
                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                      >
                        <LinkTo
                          @route="auth.basic-data.item"
                          @model={{item.id}}
                          class="text-indigo-600 hover:text-indigo-900"
                        >Menge bearbeiten<span class="sr-only">,
                            {{item.name}}</span></LinkTo>
                      </td>
                    </tr>
                  {{/each}}

                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PageSetup>
  </template>
}
